import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { from, switchMap, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);

  // Skip token for excluded URLs
  const excludedUrls = ['/assets', '.html', '.css', '.js', '.ico', 'b2clogin.com', 'ciamlogin.com'];
  const shouldSkipToken = excludedUrls.some(url => req.url.includes(url));

  if (shouldSkipToken) {
    console.log('Token Interceptor: Skipping token for excluded URL:', req.url);
    return next(req);
  }

  // Get accounts and add token if authenticated
  const accounts = msalService.instance.getAllAccounts();

  if (accounts.length === 0) {
    console.log('Token Interceptor: No authenticated accounts found, skipping token for request:', req.url);
    return next(req);
  }

  const account = accounts[0];
  console.log('Token Interceptor: Found account:', account.username);

  // Try multiple scope strategies for B2C
  const scopeStrategies = [
    // Strategy 1: API-specific scope from environment configuration
    environment.azureAdB2C.scopes,
    // Strategy 2: Use client ID as scope (common for B2C)
    [environment.azureAdB2C.clientId],
    // Strategy 3: Standard OpenID scopes with offline_access
    ['openid', 'profile', 'offline_access'],
    // Strategy 4: Just OpenID scopes
    ['openid', 'profile']
  ];

  const tryAcquireTokenWithStrategy = async (scopes: string[], strategyIndex: number): Promise<any> => {
    try {
      console.log(`Token Interceptor: Trying strategy ${strategyIndex + 1} with scopes:`, scopes);
      const response = await msalService.instance.acquireTokenSilent({
        scopes: scopes,
        account: account,
      });
      console.log(`Token Interceptor: Strategy ${strategyIndex + 1} successful. Token acquired:`, response.accessToken?.substring(0, 50) + '...');
      return response;
    } catch (error) {
      console.warn(`Token Interceptor: Strategy ${strategyIndex + 1} failed:`, error);
      throw error;
    }
  };

  // Try strategies in sequence
  const acquireToken = async (): Promise<any> => {
    for (let i = 0; i < scopeStrategies.length; i++) {
      try {
        return await tryAcquireTokenWithStrategy(scopeStrategies[i], i);
      } catch (error) {
        if (i === scopeStrategies.length - 1) {
          throw error; // Last strategy failed
        }
        // Continue to next strategy
      }
    }
  };

  return from(acquireToken()).pipe(
    switchMap((response) => {
      console.log('Token Interceptor: Adding Bearer token to request:', req.url);
      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${response.accessToken}`,
        },
      });
      // Return the request - HTTP errors will propagate to the caller, not be caught here
      return next(authReq);
    }),
    catchError((error) => {
      // This catches TOKEN ACQUISITION errors only (from acquireToken())
      // HTTP response errors (401, 500, etc.) propagate through switchMap's next() call
      // and are NOT caught here - they go to the component's error handler
      if (error?.name === 'BrowserAuthError' || error?.errorCode) {
        console.error('Token Interceptor: Token acquisition failed:', error.errorCode || error.message);
        console.log('Token Interceptor: Proceeding without token for request:', req.url);
        return next(req);
      }
      // Re-throw HTTP errors so they propagate to the caller
      throw error;
    })
  );
};
